import Phaser from 'phaser';
import { MOWER_DEFS, MOWER_IDS, type MowerDef, type MowerId } from '../../content/mowers';
import { Dir } from '../../sim/direction';
import { FX_KEY } from '../fx/Effects';
import { TILE_SIZE } from '../layout';
import { stripeFrame } from '../render/cosmeticArt';
import { ITEMS_KEY, itemFrame, type ItemIcon } from '../render/itemArt';
import { mowerFx } from '../render/mowerFx';
import { mowerTextureKey, TILES_KEY } from '../render/placeholderArt';
import { frameIndex, type TileFrameName } from '../render/tileFrames';
import { BRUSH_CUT_POWER, tallGrassPasses } from '../../sim/grid';
import { breakSummary } from '../../content/tiles';
import { label, UI } from './text';

/** The most hit pips a stat row shows (beyond that, the count). */
const MAX_HIT_PIPS = 5;
import { wrapLineCount } from './wrap';

/** Preview art is drawn at 2x: big enough to read at a glance, still crisp pixels. */
const SCALE = 2;
const TILE = TILE_SIZE * SCALE;
/** The lawn patch: 3 x 2 tiles, the mower in the middle column, mowing upward. */
export const PREVIEW_PATCH = { width: TILE * 3, height: TILE * 2 } as const;
/** Patch plus the stats column beside it. */
export const PREVIEW_HEIGHT = PREVIEW_PATCH.height;

/** FX texture frames (see Effects.ts). */
const FX = { clipLight: 0, clipDark: 1, dust: 3, bladeLight: 7, bladeDark: 8 } as const;

const FASTEST = Math.min(...MOWER_IDS.map((id) => MOWER_DEFS[id].ticksPerTile));
const MOST_FUEL = Math.max(...MOWER_IDS.map((id) => MOWER_DEFS[id].fuelTiles ?? 0));

const TURN_STYLE: Record<MowerDef['turnRule']['kind'], string> = {
  free90Pullback: 'Turns, pulls back',
  free90: 'Quarter turns',
  turnSpacing: 'Wide turns',
  freeSpin: 'Spins in place',
};

export interface MowerPreviewOptions {
  /** Stats to show (pass the upgraded mower); defaults to the base mower. */
  stats?: MowerDef;
  /** Not available yet: a dark silhouette, no animation. */
  locked?: boolean;
  /** Texture to draw instead of the mower's own (a paint cosmetic). */
  texture?: string;
  /** Show these lines instead of the stats (cosmetics). */
  lines?: readonly DetailLine[];
}

/**
 * Showcases: the big picture at the top of the garage and shop (and on level cards) of whatever
 * is selected: a mower, an item, a stripe pattern. Each draws a 96 x 64 lawn patch at (x, y)
 * with details in a column beside it, and cleans up its timers when destroyed (scene redraws).
 */

/**
 * Draws a cols x rows lawn patch into `root`, at `scale`, with a 1 px frame. `offset` shifts the
 * rows up by that many pixels (half a tile puts a row's middle on the patch's middle), cropping
 * the part rows that stick out.
 */
function lawnPatch(
  scene: Phaser.Scene,
  root: Phaser.GameObjects.Container,
  frameAt: (tx: number, ty: number) => TileFrameName,
  scale: number,
  size: { width: number; height: number } = PREVIEW_PATCH,
  offset = 0,
): void {
  const tile = TILE_SIZE * scale;
  const cols = size.width / tile;
  const rows = Math.ceil((size.height + offset) / tile);
  for (let ty = 0; ty < rows; ty++) {
    const y = ty * tile - offset;
    const top = Math.max(0, -y) / scale;
    const bottom = Math.max(0, y + tile - size.height) / scale;
    for (let tx = 0; tx < cols; tx++) {
      const img = scene.add
        .image(tx * tile, y, TILES_KEY, frameIndex(frameAt(tx, ty)))
        .setOrigin(0)
        .setScale(scale);
      if (top > 0 || bottom > 0) img.setCrop(0, top, TILE_SIZE, TILE_SIZE - top - bottom);
      root.add(img);
    }
  }
  root.add(
    scene.add
      .graphics()
      .lineStyle(1, UI.shadow, 1)
      .strokeRect(-0.5, -0.5, size.width + 1, size.height + 1),
  );
}

const GRASS: TileFrameName[] = ['grass', 'grass2', 'grass3'];
const uncut = (tx: number, ty: number): TileFrameName => GRASS[(tx * 2 + ty) % GRASS.length]!;

export interface DetailLine {
  text: string;
  color?: number;
}

/** Where the text column beside a patch starts. */
const COLUMN_X = PREVIEW_PATCH.width + 8;

/** The text column beside a patch; lines wrap and stack. */
function details(
  scene: Phaser.Scene,
  root: Phaser.GameObjects.Container,
  width: number,
  lines: readonly DetailLine[],
): void {
  let ty = 2;
  for (const line of lines) {
    const t = label(scene, COLUMN_X, ty, line.text, 1, line.color ?? UI.text);
    root.add(t.setMaxWidth(width - COLUMN_X));
    ty += wrapLineCount(line.text, width - COLUMN_X) * 10 + 2;
  }
}

/**
 * How tall a showcase drawn `width` wide is: the patch, or its text column where that runs
 * longer. Panels size to the tallest of what they can show, so the list below stays put.
 */
export function showcaseHeight(width: number, lines: readonly DetailLine[]): number {
  // Each line's glyphs end 3 px above its next line; paragraphs are 2 px further apart.
  const column = lines.reduce((h, l) => h + wrapLineCount(l.text, width - COLUMN_X) * 10 + 2, 2);
  return Math.max(PREVIEW_PATCH.height, column - 5);
}

/** The text column of a mower showcase (its stats): bars, then turning and breaking. */
export function mowerShowcaseHeight(width: number, def: MowerDef): number {
  const w = width - COLUMN_X;
  const turn = wrapLineCount(TURN_STYLE[def.turnRule.kind], w) * 10;
  const breaks = wrapLineCount(mowerBreaks(def), w) * 10;
  return Math.max(PREVIEW_PATCH.height, 40 + turn + 3 + breaks - 3);
}

const mowerBreaks = (def: MowerDef): string =>
  breakSummary(def.breakPower ?? 0).replace('Smashes', 'Breaks');

/** An item (tool or upgrade) icon at 2x, floating over uncut grass, with its details. */
export function itemPreview(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  icon: ItemIcon,
  lines: readonly DetailLine[],
  locked = false,
): Phaser.GameObjects.Container {
  const root = scene.add.container(x, y);
  lawnPatch(scene, root, uncut, SCALE);
  const cx = PREVIEW_PATCH.width / 2;
  const cy = PREVIEW_PATCH.height / 2;
  const shadow = scene.add.ellipse(cx, cy + 14, 20, 5, UI.shadow, 0.35);
  const sprite = scene.add.image(cx, cy, ITEMS_KEY, itemFrame(icon)).setScale(SCALE);
  root.add([shadow, sprite]);
  if (locked) {
    sprite.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.75);
  } else {
    scene.tweens.add({ targets: sprite, y: cy - SCALE, duration: 700, yoyo: true, repeat: -1 });
    root.once(Phaser.GameObjects.Events.DESTROY, () => scene.tweens.killTweensOf(sprite));
  }
  details(scene, root, width, lines);
  return root;
}

/** A stripe pattern cosmetic on a freshly mowed lawn (1x tiles, so the pattern shows). */
export function stripePreview(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  patternId: string,
  lines: readonly DetailLine[],
  locked = false,
): Phaser.GameObjects.Container {
  const root = scene.add.container(x, y);
  lawnPatch(scene, root, (tx, ty) => (locked ? uncut(tx, ty) : stripeFrame(patternId, tx, ty)), 1);
  if (locked) root.add(label(scene, PREVIEW_PATCH.width / 2, 24, '?', 2, UI.dim).setOrigin(0.5, 0));
  details(scene, root, width, lines);
  return root;
}

/**
 * A mower showcase: the sprite at 2x on a little lawn patch, mowing a stripe (engine bob,
 * clippings, exhaust for gas mowers), with its stats beside it as bars. Draws at (x, y) = top-left
 * and fills the given width. Cleans up its timers when destroyed (scene redraws).
 */
export function mowerPreview(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  id: MowerId,
  options: MowerPreviewOptions = {},
): Phaser.GameObjects.Container {
  const def = options.stats ?? MOWER_DEFS[id];
  const root = scene.add.container(x, y);

  // Lawn patch: uncut grass ahead, a fresh stripe behind the mower (driving east: side on). The
  // rows sit half a tile up, so the stripe runs through the middle and the mower is centered on
  // it, as it is on its row in a lawn.
  lawnPatch(
    scene,
    root,
    (tx, ty) => (tx <= 1 && ty === 1 ? 'mowedE' : uncut(tx, ty)),
    SCALE,
    PREVIEW_PATCH,
    TILE / 2,
  );

  const cx = PREVIEW_PATCH.width / 2;
  const cy = PREVIEW_PATCH.height / 2;
  const texture = options.texture ?? mowerTextureKey(id);
  const mower = scene.add.sprite(cx, cy, texture, Dir.E);
  // A mower taller than the patch at 2x (the Fairway's side reels) shows at 1x, inside it.
  const scale = mower.height * SCALE > PREVIEW_PATCH.height ? 1 : SCALE;
  mower.setScale(scale);
  root.add(mower);

  const timers: Phaser.Time.TimerEvent[] = [];
  if (options.locked) {
    mower.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.75);
    root.add(label(scene, cx, cy - 4, '?', 2, UI.dim).setOrigin(0.5));
  } else {
    // Engine bob: one art pixel, faster for faster mowers.
    const beat = Math.round(def.ticksPerTile * 6);
    scene.tweens.add({ targets: mower, y: cy - scale, duration: beat, yoyo: true, repeat: -1 });
    timers.push(
      scene.time.addEvent({
        delay: 140,
        loop: true,
        callback: () => spray(scene, root, cx, cy, id),
      }),
    );
    const exhaust = mowerFx(id, Dir.E).exhaust;
    if (def.fuelTiles !== undefined && exhaust) {
      timers.push(
        scene.time.addEvent({
          delay: 420,
          loop: true,
          callback: () => puff(scene, root, cx + exhaust.x * scale, cy + exhaust.y * scale),
        }),
      );
    }
  }

  root.once(Phaser.GameObjects.Events.DESTROY, () => {
    for (const t of timers) t.remove();
    scene.tweens.killTweensOf(mower);
  });
  if (options.lines) {
    details(scene, root, width, options.lines);
    return root;
  }

  // Stats column.
  const sx = COLUMN_X;
  const g = scene.add.graphics();
  root.add(g);
  const row = (ty: number, name: string) => root.add(label(scene, sx, ty, name, 1, UI.dim));
  const pips = (ty: number, filled: number, total: number, color: number) => {
    for (let i = 0; i < total; i++) {
      g.fillStyle(i < filled ? color : UI.shadow, i < filled ? 1 : 0.6);
      g.fillRect(sx + 34 + i * 6, ty + 1, 4, 5);
    }
  };
  const speedPips = Math.max(1, Math.round((FASTEST / def.ticksPerTile) * 5));
  row(2, 'SPEED');
  pips(2, speedPips, 5, UI.accent);
  row(14, 'TOUGH');
  // (A frame upgraded past five hits shows the count: the pips would run off the card.)
  if (def.maxHp > MAX_HIT_PIPS)
    root.add(label(scene, sx + 34, 14, `${def.maxHp} hits`, 1, 0xff6b5a));
  else pips(14, def.maxHp, Math.max(3, def.maxHp), 0xff6b5a);
  row(26, 'FUEL');
  if (def.fuelTiles === undefined) root.add(label(scene, sx + 34, 26, 'none', 1, UI.text));
  else pips(26, Math.max(1, Math.round((def.fuelTiles / MOST_FUEL) * 5)), 5, 0x7fd4ff);
  // Below the bars: the turning style, then what it smashes through (each may wrap).
  const turnText = TURN_STYLE[def.turnRule.kind];
  root.add(label(scene, sx, 40, turnText, 1, UI.text).setMaxWidth(width - sx));
  const breaksY = 40 + wrapLineCount(turnText, width - sx) * 10 + 3;
  root.add(label(scene, sx, breaksY, mowerBreaks(def), 1, UI.dim).setMaxWidth(width - sx));
  return root;
}

/** What a mower can do, in short lines (the garage's big card). */
export function mowerAbilities(def: MowerDef): string[] {
  const lines = [TURN_DETAIL[def.turnRule.kind]];
  if (def.wings) lines.push('Side reels mow either side');
  if (def.cutPower >= BRUSH_CUT_POWER) lines.push('Drives over sticks');
  const passes = tallGrassPasses(def.bite);
  lines.push(`Tall grass: ${passes} pass${passes === 1 ? '' : 'es'}`);
  if (def.throttle) lines.push('Slow gear for tight spots');
  lines.push(breakSummary(def.breakPower ?? 0));
  lines.push(def.fuelTiles === undefined ? 'No gas needed' : `Tank: ${def.fuelTiles} tiles`);
  lines.push(`${def.toolSlots} tool slot${def.toolSlots === 1 ? '' : 's'}`);
  return lines;
}

const TURN_DETAIL: Record<MowerDef['turnRule']['kind'], string> = {
  free90Pullback: 'Quarter turns + pull-back',
  free90: 'Quarter turns only',
  turnSpacing: 'Wide turns only',
  freeSpin: 'Turns any way, spins',
};

/** Height of the garage's big mower card (patch, bars, abilities) for this mower. */
export function mowerCardHeight(def: MowerDef): number {
  return CARD_PATCH_H + 6 + 3 * 11 + 2 + mowerAbilities(def).length * 9;
}
const CARD_PATCH_H = TILE * 3;

/**
 * The garage's big mower card: a wide lawn patch (room for the 3-wide Fairway Mower) with the
 * mower mowing up it, then its stat bars and, below, what it can do in plain words.
 */
export function mowerCard(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  id: MowerId,
  options: MowerPreviewOptions = {},
): Phaser.GameObjects.Container {
  const def = options.stats ?? MOWER_DEFS[id];
  const root = scene.add.container(x, y);
  const cols = Math.floor(width / TILE);
  const patch = { width: cols * TILE, height: CARD_PATCH_H };
  const px = Math.floor((width - patch.width) / 2);
  const lawn = scene.add.container(px, 0);
  root.add(lawn);
  const mid = Math.floor(cols / 2);
  lawnPatch(
    scene,
    lawn,
    (tx, ty) => (tx <= mid && ty === 1 ? 'mowedE' : uncut(tx, ty)),
    SCALE,
    patch,
  );
  const cx = px + mid * TILE + TILE / 2;
  // On its row's middle, as on a lawn.
  const cy = TILE * 1.5;
  const mower = scene.add
    .sprite(cx, cy, options.texture ?? mowerTextureKey(id), Dir.E)
    .setScale(SCALE);
  root.add(mower);
  const timers: Phaser.Time.TimerEvent[] = [];
  if (options.locked) {
    mower.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.75);
    root.add(label(scene, cx, cy - 4, '?', 2, UI.dim).setOrigin(0.5));
  } else {
    const beat = Math.round(def.ticksPerTile * 6);
    scene.tweens.add({ targets: mower, y: cy - SCALE, duration: beat, yoyo: true, repeat: -1 });
    timers.push(
      scene.time.addEvent({
        delay: 140,
        loop: true,
        callback: () => spray(scene, root, cx, cy, id),
      }),
    );
    const exhaust = mowerFx(id, Dir.E).exhaust;
    if (def.fuelTiles !== undefined && exhaust) {
      timers.push(
        scene.time.addEvent({
          delay: 420,
          loop: true,
          callback: () => puff(scene, root, cx + exhaust.x * SCALE, cy + exhaust.y * SCALE),
        }),
      );
    }
  }
  root.once(Phaser.GameObjects.Events.DESTROY, () => {
    for (const t of timers) t.remove();
    scene.tweens.killTweensOf(mower);
  });

  // Bars, then abilities (or "???" while it is still locked).
  let ty = CARD_PATCH_H + 6;
  const g = scene.add.graphics();
  root.add(g);
  const bar = (name: string, filled: number, total: number, color: number) => {
    root.add(label(scene, 2, ty, name, 1, UI.dim));
    for (let i = 0; i < total; i++) {
      g.fillStyle(i < filled ? color : UI.shadow, i < filled ? 1 : 0.6);
      g.fillRect(40 + i * 7, ty + 1, 5, 5);
    }
    ty += 11;
  };
  bar('SPEED', Math.max(1, Math.round((FASTEST / def.ticksPerTile) * 5)), 5, UI.accent);
  if (def.maxHp > MAX_HIT_PIPS) {
    root.add(label(scene, 2, ty, 'TOUGH', 1, UI.dim));
    root.add(label(scene, 40, ty, `${def.maxHp} hits`, 1, 0xff6b5a));
    ty += 11;
  } else bar('TOUGH', def.maxHp, Math.max(3, def.maxHp), 0xff6b5a);
  if (def.fuelTiles === undefined) {
    root.add(label(scene, 2, ty, 'FUEL', 1, UI.dim));
    root.add(label(scene, 40, ty, 'none', 1, UI.text));
    ty += 11;
  } else bar('FUEL', Math.max(1, Math.round((def.fuelTiles / MOST_FUEL) * 5)), 5, 0x7fd4ff);
  ty += 2;
  for (const line of options.locked ? ['???'] : mowerAbilities(def)) {
    root.add(label(scene, 2, ty, `- ${line}`, 1, UI.text).setMaxWidth(width - 4));
    ty += 9;
  }
  return root;
}

/**
 * Clippings out of the mower's discharge (mowerFx.ts, heading east): thrown up and out of its
 * chute or off its reels, arcing down to the lawn; puffing gently out of a grass bag.
 */
function spray(
  scene: Phaser.Scene,
  root: Phaser.GameObjects.Container,
  cx: number,
  cy: number,
  id: MowerId,
): void {
  const fx = mowerFx(id, Dir.E);
  const bag = fx.kind === 'bag';
  // The Fairway Mower's gang: one reel ahead of each row it cuts.
  const from =
    id === 'fairway'
      ? [-1, 0, 1].map((k) => ({ x: cx + 13 * SCALE, y: cy + k * TILE }))
      : [{ x: cx + fx.discharge.x * SCALE, y: cy + fx.discharge.y * SCALE }];
  for (const p of from) {
    for (let i = 0; i < (bag ? 1 : 2); i++) {
      const frames = [FX.clipLight, FX.clipDark, FX.bladeLight, FX.bladeDark];
      const bit = scene.add
        .image(p.x, p.y, FX_KEY, frames[Phaser.Math.Between(0, 3)])
        .setScale(SCALE / 2)
        .setAngle(Phaser.Math.Between(0, 359));
      root.add(bit);
      const vertical = Math.abs(fx.aim - 90) <= 15 || Math.abs(fx.aim - 270) <= 15;
      const lift = vertical ? 0 : fx.aim > 90 && fx.aim < 270 ? 18 : -18;
      const a = ((fx.aim + lift + Phaser.Math.Between(-24, 24)) * Math.PI) / 180;
      const dist = bag ? Phaser.Math.Between(3, 7) : Phaser.Math.Between(10, 20);
      scene.tweens.add({
        targets: bit,
        x: p.x + Math.cos(a) * dist,
        angle: bit.angle + Phaser.Math.Between(-180, 180),
        duration: bag ? 360 : 440,
      });
      // Up and out, then falling back to the lawn.
      scene.tweens.add({
        targets: bit,
        y: { from: p.y, to: p.y + Math.sin(a) * dist + (bag ? 2 : 8) },
        ease: (t: number) => t * t * 2.4 - t * 1.4,
        alpha: 0,
        duration: bag ? 360 : 440,
        onComplete: () => bit.destroy(),
      });
    }
  }
}

/** A soft exhaust puff rising from the engine. */
function puff(scene: Phaser.Scene, root: Phaser.GameObjects.Container, x: number, y: number): void {
  const p = scene.add
    .image(x, y, FX_KEY, FX.dust)
    .setScale(SCALE / 2)
    .setAlpha(0.7);
  root.add(p);
  scene.tweens.add({
    targets: p,
    y: y - 10,
    x: x - 3,
    alpha: 0,
    scale: SCALE,
    duration: 700,
    onComplete: () => p.destroy(),
  });
}
